import React, { useEffect, useRef, useState } from "react";

/**
 * useCountUp — animates a numeric value from 0 (or previous value) to target
 * over `duration` milliseconds using ease-out. The hook returns the current
 * displayable number, which the caller can format.
 *
 *  - target:  the final numeric value
 *  - duration:  ms, default 800
 *  - decimals:  fractional digits (default 0)
 *  - startOnMount: if true, animate from 0 on first render (default true)
 */
export const useCountUp = (target, { duration = 800, decimals = 0, startOnMount = true } = {}) => {
  const [display, setDisplay] = useState(startOnMount ? 0 : target);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(startOnMount ? 0 : target);

  useEffect(() => {
    if (target == null || Number.isNaN(target)) {
      setDisplay(0);
      return;
    }
    // Ease-out cubic
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const to = Number(target);
    const from = fromRef.current;
    startRef.current = null;

    const step = (ts) => {
      if (startRef.current == null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / duration);
      const current = from + (to - from) * ease(t);
      const fixed = decimals > 0
        ? Number(current.toFixed(decimals))
        : Math.round(current);
      setDisplay(fixed);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = to;
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // We intentionally re-run when target or duration changes.
  }, [target, duration, decimals]);

  return display;
};

export default useCountUp;
